// The official Alloy logo lockup, from the supplied brand kit
// ("Alloy Logo.svg" gradient, "Alloy Logo B.svg" solid, "Alloy Logo W.svg"
// white). All three source files share identical geometry, so the shapes are
// defined once here and only the paint changes per tone — that is exactly how
// the kit varies them, so a single set of paths stays authoritative.
//
// Geometry, measured off the source viewBox "0 0 356.84 112.12":
//   mark      x 0 … 152.25, y 0 … 112.12  → 1.358 : 1  (NOT square)
//   wordmark  x 167.34 … 356.84
// The mark is markedly wider than tall, so `BrandMark` treats `size` as the
// WIDTH and derives height from BRAND_MARK_ASPECT. Every square-slot caller
// (sidebar rail, QR centre knockout) therefore gets a correctly-proportioned
// mark that never exceeds the slot it was given — the same guarantee the old
// square 128×128 viewBox gave, without distorting the artwork.

const MARK_D =
  "M152.25,56.05c0,1.83-1.49,3.35-3.32,3.35h-40.99c-3.02,0-5.46,2.44-5.46,5.46h0c0,3.02,2.44,5.46,5.46,5.46h27.65c1.83,0,3.32,1.49,3.32,3.32s-1.49,3.32-3.32,3.32h-27.65c-3.02,0-5.46,2.44-5.46,5.46h0c0,3.02,2.44,5.46,5.46,5.46h14.31c1.86,0,3.35,1.49,3.35,3.32s-1.49,3.35-3.35,3.35h-31.87c-3.02,0-5.46-2.44-5.46-5.46v-20.89c0-4.86-3.94-8.79-8.79-8.79h0c-4.86,0-8.79,3.94-8.79,8.79v20.89c0,3.02-2.44,5.46-5.46,5.46h-31.9c-1.83,0-3.32-1.51-3.32-3.35s1.49-3.32,3.32-3.32h14.34c3.02,0,5.46-2.44,5.46-5.46h0c0-3.02-2.44-5.46-5.46-5.46h-27.65c-1.83,0-3.35-1.49-3.35-3.32s1.51-3.32,3.35-3.32h27.65c3.02,0,5.46-2.44,5.46-5.46h0c0-3.02-2.44-5.46-5.46-5.46H3.32c-1.83,0-3.32-1.51-3.32-3.35s1.49-3.32,3.32-3.32h49.75c3.02,0,5.46-2.44,5.46-5.46h0c0-3.02-2.44-5.46-5.46-5.46H16.66c-1.83,0-3.35-1.49-3.35-3.32s1.51-3.35,3.35-3.35h45.21c3.02,0,5.46-2.44,5.46-5.46h0c0-3.02-2.44-5.46-5.46-5.46h-31.9c-1.83,0-3.32-1.49-3.32-3.32s1.49-3.32,3.32-3.32h92.29c1.86,0,3.35,1.49,3.35,3.32s-1.49,3.32-3.35,3.32h-31.87c-3.02,0-5.46,2.44-5.46,5.46h0c0,3.02,2.44,5.46,5.46,5.46h45.21c1.83,0,3.32,1.51,3.32,3.35s-1.49,3.32-3.32,3.32h-36.45c-3.02,0-5.46,2.44-5.46,5.46h0c0,3.02,2.44,5.46,5.46,5.46h49.78c1.83,0,3.32,1.49,3.32,3.32Z";
// "A" + "l" + "l" + "o" + "y", drawn as outlines rather than live text so the
// wordmark renders identically regardless of the fonts available to the host.
const WORDMARK_D = [
  "M167.34,76.61l22.07-61.86h13.21l21.99,61.86h-14.38l-4.35-13.04h-19.98l-4.43,13.04h-14.13ZM189.82,52.2h12.2l-4.43-13.21c-.67-2.09-1.42-4.51-1.67-6.02-.25,1.42-.92,3.76-1.67,6.02l-4.43,13.21Z",
  "M227.69,76.61V13.58h12.87v63.03h-12.87Z",
  "M247.75,76.61V13.58h12.87v63.03h-12.87Z",
  "M288.63,33.56c13.29,0,22.99,8.86,22.99,22.07s-9.7,21.99-22.99,21.99-23.07-8.78-23.07-21.99,9.7-22.07,23.07-22.07ZM288.63,66.08c5.94,0,10.03-4.18,10.03-10.53s-4.1-10.45-10.03-10.45-10.12,4.18-10.12,10.45,4.1,10.53,10.12,10.53Z",
  "M311.53,85.89h7.11c4.35,0,6.19-1.34,7.86-5.85l.75-2.01-16.8-43.22h13.79l9.11,26.84,10.12-26.84h13.37l-21.23,50.91c-3.51,8.53-8.19,12.12-15.55,12.12-3.09,0-6.02-.42-8.53-1.17v-10.78Z",
];

/** Mark-only bounds, and lockup bounds, straight from the kit's viewBox. */
export const BRAND_MARK_ASPECT = 152.25 / 112.12;
const LOCKUP_ASPECT = 356.84 / 112.12;

/** "brand" = the kit's purple gradient; "inverse" = its solid white variant. */
export type BrandTone = "brand" | "inverse";

const STOPS: Record<BrandTone, [string, string]> = {
  brand: ["#6300ff", "#1f00ff"],
  inverse: ["#ffffff", "#ffffff"],
};

/**
 * Gradient ids are keyed by tone rather than generated per instance. Two logos
 * of the same tone on one page therefore collide on the id but resolve to
 * identical stops (visually correct), while logos of *different* tones get
 * distinct ids and can't steal each other's paint — which is the failure a
 * single shared id would cause on a page showing both the gradient header
 * logo and the white brand-panel logo.
 */
function Defs({ tone }: { tone: BrandTone }) {
  const [from, to] = STOPS[tone];
  return (
    <linearGradient
      id={`alloy-logo-fill-${tone}`}
      gradientUnits="userSpaceOnUse"
      x1="76.12"
      y1="-5.58"
      x2="76.12"
      y2="116.83"
    >
      <stop offset="0" stopColor={from} />
      <stop offset="1" stopColor={to} />
    </linearGradient>
  );
}

/** Mark artwork only (the two bars are part of the mark, not a separate rule). */
function MarkShapes({ fill }: { fill: string }) {
  return (
    <>
      <path fill={fill} d={MARK_D} />
      <rect fill={fill} x="39.77" y="0" width="72.7" height="6.66" rx="3.33" />
      <rect fill={fill} x="39.77" y="105.46" width="72.7" height="6.66" rx="3.33" />
    </>
  );
}

/**
 * The Alloy mark on its own — for slots that already render the name
 * separately (the collapsed sidebar rail, the QR code's centre knockout).
 * `size` is the rendered WIDTH; height follows the artwork's real aspect.
 */
export function BrandMark({
  size = 36,
  tone = "brand",
}: {
  size?: number;
  tone?: BrandTone;
}) {
  const height = size / BRAND_MARK_ASPECT;
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 152.25 112.12"
      aria-hidden
      role="img"
      style={{ display: "block" }}
    >
      <defs>
        <Defs tone={tone} />
      </defs>
      <MarkShapes fill={`url(#alloy-logo-fill-${tone})`} />
    </svg>
  );
}

/**
 * The full lockup — mark plus "Alloy" wordmark — from one asset, so the two
 * halves can never drift apart the way a mark component plus separately
 * styled text can.
 *
 * `compact` is the shell's density variant (the existing call-site prop,
 * kept as-is): it renders the lockup one step smaller for tight chrome.
 * `tone` keeps its existing values — `inverse` selects the kit's solid white
 * artwork for dark surfaces (the auth showcase panel); `default` is the kit's
 * purple gradient.
 */
export function BrandLockup({
  compact = false,
  tone = "default",
}: {
  compact?: boolean;
  /** `inverse` for use on a dark surface (e.g. the auth showcase panel). */
  tone?: "default" | "inverse";
}) {
  const brandTone: BrandTone = tone === "inverse" ? "inverse" : "brand";
  const height = compact ? 28 : 36;
  return (
    <svg
      width={height * LOCKUP_ASPECT}
      height={height}
      viewBox="0 0 356.84 112.12"
      role="img"
      aria-label="Alloy"
      style={{ display: "block" }}
    >
      <defs>
        <Defs tone={brandTone} />
      </defs>
      <MarkShapes fill={`url(#alloy-logo-fill-${brandTone})`} />
      {WORDMARK_D.map((d) => (
        <path key={d.slice(0, 12)} fill={`url(#alloy-logo-fill-${brandTone})`} d={d} />
      ))}
    </svg>
  );
}
